import posthog from 'posthog-js'
import useAuthStore from '@/stores/useAuthStore'
import useConsentStore from '@/stores/useConsentStore'

/**
 * Detect if the current device is mobile (FR-43).
 * Uses User Agent detection for robust mobile device identification.
 * This method detects actual mobile devices regardless of screen size.
 */
export function isMobileDevice(): boolean {
  if (typeof window === 'undefined' || typeof navigator === 'undefined') return false
  
  const userAgent = navigator.userAgent || navigator.vendor || (window as any).opera
  
  // Check for mobile devices using user agent patterns
  const mobileRegex = /android|webos|iphone|ipad|ipod|blackberry|iemobile|opera mini|mobile|tablet/i
  
  // Additional checks for specific mobile platforms
  const isAndroid = /android/i.test(userAgent)
  const isIOS = /iphone|ipad|ipod/i.test(userAgent)
  const isWindowsPhone = /windows phone/i.test(userAgent)
  const isBlackBerry = /blackberry/i.test(userAgent)
  const isOpera = /opera mini/i.test(userAgent)
  
  // Combine checks: user agent match OR specific platform detection
  const isMobile = mobileRegex.test(userAgent) || 
                   isAndroid || isIOS || isWindowsPhone || isBlackBerry || isOpera
  
  return isMobile
}

/**
 * Update PostHog configuration based on current organization settings and user consent.
 * Uses opt_in/opt_out for reliable tracking control.
 * 
 * FR-08: Respects posthogEnabled flag per organization
 * FR-10: Respects posthogMobileEnabled flag per organization
 * FR-48: Respects user consent preferences
 */
export function updatePostHogConfig(): boolean {
  const user = useAuthStore.getState().user
  const { analyticsConsent } = useConsentStore.getState()
  
  // FR-48: Check user consent first - if user explicitly declined, respect that
  if (analyticsConsent === false) {
    posthog.opt_out_capturing()
    posthog.set_config({ autocapture: false, capture_pageview: false })
    posthog.stopSessionRecording()
    return false
  }
  
  // If user hasn't been asked yet (analyticsConsent === null), allow org settings to decide
  // This maintains backward compatibility
  
  // FR-08: Check if PostHog is enabled for this organization
  const posthogEnabled = user?.companyMember?.company?.posthogEnabled ?? true
  
  const isMobile = isMobileDevice()
  
  // FR-10: Check if mobile tracking is enabled for this organization
  const posthogMobileEnabled = user?.companyMember?.company?.posthogMobileEnabled ?? true
  
  // Determine if tracking should be enabled based on org settings and consent
  // analyticsConsent can be true, false, or null (not asked yet)
  const shouldTrack = posthogEnabled && (!isMobile || posthogMobileEnabled) && (analyticsConsent === true || analyticsConsent === null)
  
  // Use opt_in/opt_out for reliable tracking control
  if (!shouldTrack) {
    posthog.opt_out_capturing()
    posthog.set_config({ autocapture: false, capture_pageview: false })
    posthog.stopSessionRecording()
    return false
  } else {
    // Re-enable tracking if settings allow AND user consented (or hasn't been asked)
    posthog.set_config({ autocapture: true, capture_pageview: true })
    posthog.opt_in_capturing()
    posthog.startSessionRecording()
    return true
  }
}

/**
 * Central analytics capture helper (FR-11).
 * All product event tracking must go through this function.
 * 
 * FR-08: Respects posthogEnabled flag per organization
 * FR-09: Filters events by user role based on posthogRolesEnabled
 * FR-10: Respects posthogMobileEnabled flag per organization
 * FR-43: Detects mobile sessions automatically
 * FR-44: Skips mobile events if posthogMobileEnabled is false
 * FR-45: Includes is_mobile property on all events
 */
export function capture(event: string, properties?: Record<string, unknown>): void {
  // Update PostHog config based on current settings (affects autocapture too)
  const shouldTrack = updatePostHogConfig()
  
  if (!shouldTrack) {
    return
  }
  
  const user = useAuthStore.getState().user
  const isMobile = isMobileDevice()
  
  // FR-09: Check if user's role is allowed to be tracked
  const posthogRolesEnabled = (user?.companyMember?.company?.posthogRolesEnabled as string[]) ?? []
  if (Array.isArray(posthogRolesEnabled) && posthogRolesEnabled.length > 0) {
    // Determine user role (same logic as in posthog-identification.ts)
    let role = 'employee'
    if (user?.companyMember?.isAdmin || user?.companyMember?.isOwner) {
      role = 'hr'
    } else if (user?.vendorMember) {
      role = 'vendor'
    }
    
    // Skip if role not in allowed list
    if (!posthogRolesEnabled.includes(role)) {
      return
    }
  }
  
  // FR-45: Add is_mobile to all events
  const enrichedProperties = {
    ...properties,
    is_mobile: isMobile,
  }
  
  posthog.capture(event, enrichedProperties)
}

/**
 * Opt out of PostHog capturing (FR-48).
 * Call this when user withdraws consent via consent management flow.
 */
export function optOutCapturing(): void {
  posthog.opt_out_capturing()
  posthog.set_config({ autocapture: false, capture_pageview: false })
  posthog.stopSessionRecording()
}

/**
 * Opt in to PostHog capturing (FR-48).
 * Call this when user provides consent via consent management flow.
 */
export function optInCapturing(): void {
  posthog.set_config({ autocapture: true, capture_pageview: true })
  posthog.opt_in_capturing()
  posthog.startSessionRecording()
}
